import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Breadcrumb, Button, Col, Drawer, Form, Input, Modal, Select, Space } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Link, useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'

import {
  carsUrl,
  postRequest,
  carModelsUrl,
  carMakesUrl,
  statusesUrl,
  getRequest,
  conditionsUrl,
  originsUrl,
  successNotification,
  errorNotification
} from '../../config'
import FilterTable from '../../components/Forms/searchFilter'
import PageLoader from '../../components/Layouts/PageLoader'
import SelectInput from '../../components/Forms/SelectInput'
import WithData from 'hocs/WithData'
import PageTitle from 'components/PageTitle'
import Update from './Update'
import { columns } from './tableColumns'
import CarImageViewer from './CarImageViewer'
import CarDetailBody from './CarDetailBody'
import ActiveFilters from './ActiveFilters'
import {
  FILTER_KEYS,
  PAGE_SIZES,
  activeCount,
  buildQuery,
  parseQuery,
  toSearchBody,
  withPage,
  withPageSize,
  withoutFilter
} from './query'

const { confirm } = Modal

const YEARS = (() => {
  const years = []
  for (let i = new Date().getFullYear() + 1; i >= 1960; i--) years.push({ value: i, label: i })
  return years
})()

const POSTED_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
]

const toOptions = (rows, label) => rows.map(row => ({ value: row.id, label: label(row) }))

/**
 * The cars list (spec: cars-list-filters-in-url).
 *
 * The URL is the only place the filters and the page live. This component reads `location.search`
 * and fetches; every control — Apply, a chip's ×, paging, the page size, Drafts, the search box —
 * writes a new query with `history.push`. So a refresh, Back/Forward, a new tab and a link pasted
 * into Telegram all restore the exact view, with no code for any of them. The one piece of state
 * that is not the URL is the drawer's draft, which only becomes the URL on Apply.
 */
const Car = () => {
  const history = useHistory()
  const location = useLocation()
  const query = useMemo(() => parseQuery(location.search), [location.search])
  const { filters, page, pageSize } = query

  const [cars, setCars] = useState([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })

  const [makes, setMakes] = useState([])
  const [modelsByMake, setModelsByMake] = useState({})
  const [statuses, setStatuses] = useState([])
  const [conditions, setConditions] = useState([])
  const [origins, setOrigins] = useState([])

  const [showModal, setShowModal] = useState(false)
  const [modalKey, setModalKey] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [drawerKey, setDrawerKey] = useState(0)
  const [draftMake, setDraftMake] = useState(undefined)
  const [currentCar, setCurrentCar] = useState({})
  const [showCarImages, setShowCarImages] = useState(false)
  // Quick view: the same detail body the routed page renders, in a modal. Reading
  // one car should not cost a whole new tab.
  const [quickViewId, setQuickViewId] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [form] = Form.useForm()

  const navigate = useCallback(search => history.push({ pathname: '/cars', search }), [history])

  // -- the one fetch -------------------------------------------------------------------------
  const load = useCallback(async () => {
    const response = await postRequest(
      `${carsUrl}/search?page=${page}&page_size=${pageSize}`,
      toSearchBody(filters),
      'cars',
      setLoading
    )
    if (response.success) {
      setCars(response.data.results)
      setMeta({ ...response.data, pageSize: response.data.pageSize || pageSize })
    }
  }, [filters, page, pageSize])

  useEffect(() => {
    load()
    setSelectedRowKeys([])
  }, [load])

  // -- the option lists ------------------------------------------------------------------------
  useEffect(() => {
    const quiet = () => {}
    getRequest(`${carMakesUrl}/all`, 'makes', quiet).then(r => {
      if (r.success) setMakes(toOptions(r.data, m => `${m.name} (${m.cars})`))
    })
    getRequest(`${statusesUrl}/all`, 'statuses', quiet).then(r => {
      if (r.success) setStatuses(toOptions(r.data, s => s.name))
    })
    getRequest(`${conditionsUrl}/all`, 'conditions', quiet).then(r => {
      if (r.success) setConditions(toOptions(r.data, c => c.name))
    })
    getRequest(`${originsUrl}/all`, 'origins', quiet).then(r => {
      if (r.success) setOrigins(toOptions(r.data, o => o.country))
    })
  }, [])

  // Models are loaded per make, for the make in the URL (the chip) and the make in the drawer.
  const loadModels = useCallback(
    async make => {
      if (!make || modelsByMake[make]) return
      const r = await postRequest(`${carModelsUrl}/for-make`, { make }, 'models', () => {})
      if (r.success) {
        setModelsByMake(prev => ({ ...prev, [make]: toOptions(r.data, m => `${m.name} (${m.cars})`) }))
      }
    },
    [modelsByMake]
  )
  useEffect(() => {
    loadModels(filters.make)
  }, [filters.make, loadModels])
  useEffect(() => {
    loadModels(draftMake)
  }, [draftMake, loadModels])

  const options = useMemo(
    () => ({ makes, models: filters.make ? modelsByMake[filters.make] : [], statuses, conditions, origins }),
    [makes, modelsByMake, filters.make, statuses, conditions, origins]
  )

  // -- the drawer --------------------------------------------------------------------------------
  const openFilters = () => {
    setDraftMake(filters.make)
    setDrawerKey(k => k + 1) // remount, so every control starts from the URL's values
    setShowFilters(true)
  }
  const applyFilters = () => {
    const draft = form.getFieldsValue()
    const next = {}
    FILTER_KEYS.forEach(key => {
      if (draft[key] !== undefined && draft[key] !== null && String(draft[key]).trim() !== '') {
        next[key] = String(draft[key])
      }
    })
    if (filters.q) next.q = filters.q // the search box is not in the drawer; keep it
    setShowFilters(false)
    navigate(buildQuery(next, 1, pageSize))
  }

  const showDrafts = () => {
    const hidden = statuses.find(s => String(s.label).toLowerCase() === 'unavailable')
    if (!hidden) return
    navigate(buildQuery({ ...filters, status: hidden.value }, 1, pageSize))
  }

  // -- row actions ---------------------------------------------------------------------------
  const handleSuccess = car => setCars(prev => prev.map(c => (c.id === car.id ? car : c)))

  useEffect(() => {
    if (showModal) setModalKey(k => k + 1)
  }, [showModal])

  const handleBulkDelete = () => {
    confirm({
      title: `Are you sure you want to delete ${selectedRowKeys.length} cars?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone and will delete associated images and documents.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        const response = await postRequest(
          `${carsUrl}/delete-multiple/`,
          { ids: selectedRowKeys },
          'bulk delete cars',
          setLoading
        )
        if (response.success) {
          successNotification('Cars Deleted Successfully')
          setSelectedRowKeys([])
          load() // the same page of the same filters, minus what was deleted
        } else if (response.error.response) {
          errorNotification(response.error.response.data.message)
        }
      }
    })
  }

  const count = activeCount(filters)

  return (
    <PageLoader className="container" loading={loading}>
      <PageTitle title="Cars" />
      <div className="row">
        <div className="col-lg-12">
          <Breadcrumb>
            <Breadcrumb.Item href="/">
              <span>Home</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Cars</Breadcrumb.Item>
          </Breadcrumb>
          <br />
          <br />
          <div className="card mb-4rem">
            <div className="card-header">
              <div className="utils__title">
                <span>Cars {meta && `(${meta.count})`}</span>
              </div>
            </div>
            <div className="card-body">
              <Space wrap style={{ marginBottom: '1rem' }}>
                <Button type="primary">
                  <Link to="/cars/add-new" target="_blank" rel="noopener noreferrer">
                    Add New Car
                  </Link>
                </Button>

                <Button type="default" onClick={openFilters}>
                  Apply Filters{count ? ` (${count})` : ''}
                </Button>

                {/* Cars the Telegram bot or a scrape just added land hidden (unavailable) and
                    are finished from here: the same list, narrowed to them. */}
                <Button type="default" onClick={showDrafts}>
                  Drafts
                </Button>

                {selectedRowKeys.length > 0 && (
                  <Button type="primary" danger onClick={handleBulkDelete}>
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                )}

                {/* A chassis number, a car's id or slug, or words like "changan uni". Keyed on the
                    URL's value so Back/Forward redraw the box; applied on Enter or the icon. */}
                <Input.Search
                  key={filters.q || ''}
                  defaultValue={filters.q || ''}
                  placeholder="Search chassis, id, make, model…"
                  allowClear
                  style={{ width: 280 }}
                  onSearch={value =>
                    navigate(buildQuery({ ...filters, q: (value || '').trim() }, 1, pageSize))
                  }
                />

                <Select
                  value={pageSize}
                  options={PAGE_SIZES.map(n => ({ value: n, label: `${n} per page` }))}
                  onChange={size => navigate(withPageSize(location.search, size))}
                  style={{ width: 130 }}
                />
              </Space>

              <ActiveFilters
                filters={filters}
                options={options}
                onRemove={key => navigate(withoutFilter(location.search, key))}
                onClear={() => navigate('')}
              />

              <FilterTable
                dataSource={cars}
                columns={columns(setShowModal, setCurrentCar, setShowCarImages, setQuickViewId)}
                meta={meta}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                onPageChange={next => navigate(withPage(location.search, next))}
              />

              <CarImageViewer
                carId={currentCar.id}
                visible={showCarImages}
                onClose={() => setShowCarImages(false)}
              />

              {/* destroyOnClose matters: the body fetches on mount, so closing and
                  reopening a row shows current data rather than what it held when
                  the modal was first opened. */}
              <Modal
                title="Car Details"
                open={!!quickViewId}
                onCancel={() => setQuickViewId(null)}
                footer={null}
                width={980}
                style={{ top: 16 }}
                destroyOnClose
              >
                {quickViewId ? <CarDetailBody id={quickViewId} mode="modal" /> : null}
              </Modal>

              <Modal
                title="Update Car Details"
                centered
                open={showModal}
                onOk={() => setShowModal(false)}
                onCancel={() => setShowModal(false)}
                footer={null}
                width={700}
              >
                <Update
                  key={modalKey}
                  car={currentCar}
                  handleCancel={car => {
                    setShowModal(false)
                    handleSuccess(car)
                  }}
                />
              </Modal>

              <Drawer
                title="Apply Filters"
                placement="right"
                open={showFilters}
                closable
                onClose={() => setShowFilters(false)}
              >
                <div className="my-4">
                  {/* Remounted on every open (`drawerKey`), so the controls start from what the
                      URL says and a cancelled draft leaves nothing behind. */}
                  <Form key={drawerKey} form={form} style={{ margin: 0, padding: 0 }} layout="vertical">
                    <div className="row">
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Car Make"
                          name="make"
                          value={filters.make}
                          onChange={value => {
                            setDraftMake(value)
                            form.setFieldsValue({ model: undefined }) // a model belongs to a make
                          }}
                          optionList={makes}
                          required={false}
                          allowClear
                        />
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Car Model"
                          name="model"
                          value={filters.model}
                          optionList={(draftMake && modelsByMake[draftMake]) || []}
                          required={false}
                          allowClear
                          disabled={!draftMake}
                        />
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Min Year"
                          name="minYear"
                          value={filters.minYear ? Number(filters.minYear) : undefined}
                          optionList={YEARS}
                          required={false}
                          allowClear
                        />
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Max Year"
                          name="maxYear"
                          value={filters.maxYear ? Number(filters.maxYear) : undefined}
                          optionList={YEARS}
                          required={false}
                          allowClear
                        />
                      </div>
                      <div className="col-md-12">
                        <Form.Item label="Min Price" name="minPrice" initialValue={filters.minPrice}>
                          <Input placeholder="Min Price" type="number" />
                        </Form.Item>
                      </div>
                      <div className="col-md-12">
                        <Form.Item label="Max Price" name="maxPrice" initialValue={filters.maxPrice}>
                          <Input placeholder="Max Price" type="number" />
                        </Form.Item>
                      </div>
                      <div className="col-md-12">
                        <Form.Item label="Max Mileage" name="maxMileage" initialValue={filters.maxMileage}>
                          <Input placeholder="Max Mileage" type="number" />
                        </Form.Item>
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Condition"
                          name="condition"
                          value={filters.condition}
                          optionList={conditions}
                          allowClear
                          required={false}
                        />
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Posted On Social Media"
                          name="posted"
                          value={filters.posted}
                          optionList={POSTED_OPTIONS}
                          allowClear
                          required={false}
                        />
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Origin"
                          name="origin"
                          value={filters.origin}
                          optionList={origins}
                          allowClear
                          required={false}
                        />
                      </div>
                      <div className="col-md-12">
                        <SelectInput
                          placeholder="select"
                          label="Status"
                          name="status"
                          value={filters.status}
                          optionList={statuses}
                          allowClear
                          required={false}
                        />
                      </div>
                    </div>

                    <Col md={24}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
                        <Space>
                          <Button
                            onClick={() => {
                              form.resetFields()
                              FILTER_KEYS.forEach(key => form.setFieldsValue({ [key]: undefined }))
                              setDraftMake(undefined)
                            }}
                          >
                            Reset
                          </Button>
                          <Button type="primary" onClick={applyFilters}>
                            Apply
                          </Button>
                        </Space>
                      </div>
                    </Col>
                  </Form>
                </div>
              </Drawer>
            </div>
          </div>
        </div>
      </div>
    </PageLoader>
  )
}

Car.propTypes = {
  cars: PropTypes.array,
  setCars: PropTypes.func
}

export default WithData(Car)
